import { useEffect, useState, useMemo, useRef } from "react";
import { api } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { parseReminderDays, fetchSheetData, selectMostRecentSheet, autoSelectColumns, isPresetPeriodicity, normalizePeriodicityUnit, validatePeriodicityValue, getCustomPeriodicityMax } from "./renewalUtils";
import { useDefaultCountryCode } from "@/hooks/useDefaultCountryCode";
import {
  buildMetaTemplateRequest,
  buildSheetColumnMapping,
  MetaTemplateButtonInput,
  normalizeMetaTemplateName,
  validateMetaTemplateDraft,
} from "@/lib/metaTemplateUtils";
import {
  normalizeMetaTemplate,
  normalizeLocalTemplate,
} from "@/lib/templatePreview";

type Step = "info" | "template" | "data" | "message" | "preview" | "dynamic_config" | "calendar";

type PreCachedAssets = {
  sheets: any[];
  templates: any[];
  metaTemplates: any[];
} | null;

export const useRenewalCreate = (
  onCancel?: () => void,
  onCreated?: (id: string) => void,
  initialData?: any,
  preCachedAssets?: PreCachedAssets
) => {
  const [step, setStep] = useState<Step>("info");
  const [saving, setSaving] = useState(false);

  // Form states
  const [name, setName] = useState("");
  const [googleSheetId, setGoogleSheetId] = useState("");
  const [googleSheetTabId, setGoogleSheetTabId] = useState<number | null>(null);
  const [googleSheetTabName, setGoogleSheetTabName] = useState("");
  const [phoneColumn, setPhoneColumn] = useState<string>("");
  const [renewalDateColumn, setRenewalDateColumn] = useState<string>("");
  const [dynamicDateColumn, setDynamicDateColumn] = useState<string>("");
  const [dynamicTimeColumn, setDynamicTimeColumn] = useState<string>("");
  
  const [sheetColumns, setSheetColumns] = useState<string[] | null>(null);
  const [sampleRow, setSampleRow] = useState<Record<string, any> | null>(null);

  const [columnCheck, setColumnCheck] = useState<{
    sheetId: string;
    tabName: string;
    tabId: number | null;
    status: "loading" | "loaded" | "error";
  } | null>(null);

  const columnRequestRef = useRef(0);

    
  const columnValidationStatus: "idle" | "loading" | "loaded" | "error" =
    !googleSheetId || !googleSheetTabName
      ? "idle"
      : columnCheck?.sheetId === googleSheetId &&
          columnCheck.tabName === googleSheetTabName &&
          columnCheck.tabId === (googleSheetTabId ?? null)
        ? columnCheck.status
        : "loading";


  const { defaultCountryCode } = useDefaultCountryCode();
  const [countryCode, setCountryCode] = useState("+57");
  const [messageTemplate, setMessageTemplate] = useState("");
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [campaignType, setCampaignType] = useState<string>("");
  const [nameConflictError, setNameConflictError] = useState<string | null>(null);
  const [checkingCampaignName, setCheckingCampaignName] = useState(false);
  const [periodicityValue, setPeriodicityValue] = useState<number>(5); // ✅ Cambiado de 1 a 5
  const [periodicityUnit, setPeriodicityUnit] = useState<"MONTHS" | "YEARS" | "DAYS" | "WEEKS">("MONTHS");
  const [isCustomPeriodicity, setIsCustomPeriodicity] = useState<boolean>(false);
  const [reminderDays, setReminderDays] = useState<number[]>([]);
  const [reminderInput, setReminderInput] = useState<string>("");
  const [sendingTime, setSendingTime] = useState("09:00");

  // Template specific metadata (persisted for creation flow)
  const [templateType, setTemplateType] = useState<string>("Utilidad: Alertas y actualizaciones");
  const [templateLanguage, setTemplateLanguage] = useState<string>("Español");
  const [templateNameInput, setTemplateNameInput] = useState<string>("");

  // ✅ Template components (header, footer, buttons) - persisted for creation flow
  const [templateHeaderText, setTemplateHeaderText] = useState<string>("");
  const [templateFooterText, setTemplateFooterText] = useState<string>("");
  const [templateButtons, setTemplateButtons] = useState<MetaTemplateButtonInput[]>([]);

  // ✅ Header image support
  const [templateHeaderType, setTemplateHeaderType] = useState<'TEXT' | 'IMAGE'>('TEXT');
  const [templateHeaderImageHandle, setTemplateHeaderImageHandle] = useState<string>('');
  const [templateHeaderImageUrl, setTemplateHeaderImageUrl] = useState<string>(''); // ✅ GCS public URL
  const [templateHeaderImagePreview, setTemplateHeaderImagePreview] = useState<string>('');

  // ✅ Toggle states for template sections (persist across navigation)
  const [templateHeaderEnabled, setTemplateHeaderEnabled] = useState<boolean>(false);
  const [templateFooterEnabled, setTemplateFooterEnabled] = useState<boolean>(false);
  const [templateButtonsEnabled, setTemplateButtonsEnabled] = useState<boolean>(false);

  // Assets
  const [sheets, setSheets] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(!preCachedAssets);

  // Marketing-specific states
  const [metaTemplates, setMetaTemplates] = useState<{ name: string; language: string; status: string; category: string; body?: string }[]>([]);
  const [loadingMetaTemplates, setLoadingMetaTemplates] = useState(false);
  const [selectedMetaTemplate, setSelectedMetaTemplate] = useState("");
  const [sendDate, setSendDate] = useState("");
  const [sendImmediately, setSendImmediately] = useState(false);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [templatesInUseForSheet, setTemplatesInUseForSheet] = useState<string[]>([]);
  const [tabNamesInUseForSheet, setTabNamesInUseForSheet] = useState<string[]>([]);
  const [tabIdsInUseForSheet, setTabIdsInUseForSheet] = useState<number[]>([]);

  // UI states
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 6;

  // ✅ Estado para el modal de aprobación pendiente
  const [showPendingApprovalModal, setShowPendingApprovalModal] = useState(false);
  const [pendingApprovalInfo, setPendingApprovalInfo] = useState<{
    campaignName: string;
    templateName: string;
    campaignType: "Marketing" | "Renovación" | "Dinámica" | "Calendar";
  } | null>(null);
  const [pendingCampaignId, setPendingCampaignId] = useState<string | null>(null);

  // ✅ NUEVO: Estados para Modo Citas (Dynamic Campaigns)
  const [isAppointmentMode, setIsAppointmentMode] = useState(false);
  const [useDefaultReminders, setUseDefaultReminders] = useState(false);
  const [dynamicReminderDays, setDynamicReminderDays] = useState<number[]>([]);
  const [dynamicReminderHours, setDynamicReminderHours] = useState<number[]>([]);

  // ✅ Sync default country code from WhatsApp connection
  useEffect(() => {
    setCountryCode(defaultCountryCode);
  }, [defaultCountryCode]);

  // ✅ Detectar retorno de Google Sheets o Google Calendar y restaurar el wizard
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromGoogleSheets = urlParams.get('from') === 'google-sheets';
    const fromGoogleCalendar = urlParams.get('from') === 'google-calendar';
    const stepParam = urlParams.get('step');

    const savedStep = localStorage.getItem('renewal_create_initial_step');
    const preselectedSheetId = localStorage.getItem('preselected_sheet_id');
    const savedCampaignType = localStorage.getItem('renewal_create_campaign_type');
    const savedCampaignName = localStorage.getItem('renewal_create_campaign_name');

    const shouldGoToCalendarStep =
      fromGoogleCalendar || savedStep === 'calendar' || stepParam === 'calendar';
    const shouldGoToDataStep =
      fromGoogleSheets || savedStep === 'data' || stepParam === 'data' || stepParam === '2';

    if (
      shouldGoToCalendarStep ||
      shouldGoToDataStep ||
      preselectedSheetId ||
      savedCampaignType ||
      savedCampaignName
    ) {
      console.log(
        shouldGoToCalendarStep
          ? '🔄 Configurando estado inicial desde Google Calendar...'
          : '🔄 Configurando estado inicial desde Google Sheets...'
      );

      // Restaurar el nombre de la campaña
      if (savedCampaignName) {
        console.log('📋 Restaurando nombre de campaña:', savedCampaignName);
        setName(savedCampaignName);
        localStorage.removeItem('renewal_create_campaign_name');
      }

      // Restaurar el tipo de campaña
      if (savedCampaignType) {
        console.log('📋 Restaurando tipo de campaña:', savedCampaignType);
        setCampaignType(savedCampaignType);
        localStorage.removeItem('renewal_create_campaign_type');
      }

      if (shouldGoToCalendarStep) {
        console.log('📍 Estableciendo paso inicial: calendar');
        setCampaignType('Calendar');
        setStep('calendar');
      } else if (shouldGoToDataStep) {
        if (savedCampaignType === 'Dynamic') {
          console.log('📍 Estableciendo paso inicial: dynamic_config');
          setStep('dynamic_config');
        } else {
          console.log('📍 Estableciendo paso inicial: data');
          setStep('data');
        }
      }

      // Preseleccionar la hoja guardada (not for Calendar — calendar id is not a sheet)
      if (preselectedSheetId && savedCampaignType !== 'Calendar') {
        console.log('📋 Preseleccionando hoja:', preselectedSheetId);
        setGoogleSheetId(preselectedSheetId);
        setGoogleSheetTabId(null);
        setGoogleSheetTabName("");
        setSheetColumns(null);
        setSampleRow(null);
        setPhoneColumn("");
        setRenewalDateColumn("");

        // Limpiar localStorage
        localStorage.removeItem('preselected_sheet_id');
      }

      // Limpiar localStorage del paso
      localStorage.removeItem('renewal_create_initial_step');

      if (fromGoogleSheets || fromGoogleCalendar || stepParam) {
        urlParams.delete('from');
        urlParams.delete('step');
        const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, []);

  // Derived values
  const sortedSheets = useMemo(() => {
    return [...sheets].sort((a: any, b: any) => {
      const aName = (a.sheet_name || a.name || "").toLowerCase();
      const bName = (b.sheet_name || b.name || "").toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [sheets]);
  const total = sortedSheets.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const start = (currentPage - 1) * itemsPerPage;
  const visibleSheets = sortedSheets.slice(start, start + itemsPerPage);

  // ✅ Filter out templates already in use for the selected sheet
  const availableMetaTemplates = useMemo(() => {
    if (!templatesInUseForSheet.length) return metaTemplates;
    return metaTemplates.filter((t: any) => !templatesInUseForSheet.includes(t.id));
  }, [metaTemplates, templatesInUseForSheet]);

  // ✅ Si hay assets pre-cargados, usarlos inmediatamente
  useEffect(() => {
    if (preCachedAssets) {
      console.log("⚡ Usando assets pre-cargados");

      // ✅ Show all sheets (no longer filtering by usage)
      setSheets(preCachedAssets.sheets);
      console.log(`📋 Hojas pre-cargadas: ${preCachedAssets.sheets.length}`);
      setTemplates(preCachedAssets.templates);

      // Procesar Meta templates
      if (preCachedAssets.metaTemplates?.length > 0) {
        const processed = preCachedAssets.metaTemplates
          .filter((t: any) => t.status === 'APPROVED')
          .map((t: any) =>
            normalizeMetaTemplate(t, preCachedAssets.templates)
          );
        setMetaTemplates(processed);
      }
      setLoadingAssets(false);
    }
  }, [preCachedAssets]);

  // ✅ Persistencia inteligente: Sincronizar metadatos cuando cambia la plantilla seleccionada
  useEffect(() => {
    if (!templateId) {
      setTemplateHeaderImageUrl('');
      setTemplateHeaderImagePreview('');
      setTemplateHeaderType('TEXT');
      return;
    }

    // Buscar la plantilla en metaTemplates o en la lista general
    let template: any = metaTemplates.find((t: any) => String(t.id) === String(templateId));
    if (!template) {
      template = templates.find((t: any) => String(t.id || t._id) === String(templateId));
    }

    if (template) {
      // 1. Manejo de Imagen de Header
      if (template.header_image_url) {
        setTemplateHeaderImageUrl(template.header_image_url);
        setTemplateHeaderImagePreview(template.header_image_url);
        setTemplateHeaderType('IMAGE');
        setTemplateHeaderEnabled(true);
      } else if (template.header_text) {
        setTemplateHeaderText(template.header_text);
        setTemplateHeaderType('TEXT');
        setTemplateHeaderEnabled(true);
      }

      // 2. Manejo de Footer
      if (template.footer_text) {
        setTemplateFooterText(template.footer_text);
        setTemplateFooterEnabled(true);
      }

      // 3. Manejo de Botones
      if (template.buttons && template.buttons.length > 0) {
        setTemplateButtons([...(template.buttons as MetaTemplateButtonInput[])]);
        setTemplateButtonsEnabled(true);
      }
    }
  }, [templateId, metaTemplates, templates]);

  // Load assets (solo si NO hay assets pre-cargados)
  useEffect(() => {
    if (preCachedAssets) return; // ✅ Saltar si ya tenemos assets pre-cargados

    (async () => {
      try {
        setLoadingAssets(true);

        const [sRes, tRes, mRes] = await Promise.allSettled([
          api.get("/google-sheets"),
          api.get("/templates"),
          api.get("/whatsapp/templates/meta/list")
        ]);

        // Mostrar hojas inmediatamente (sin filtrar)
        let loadedSheets = sRes.status === "fulfilled" ? (sRes.value.data || []) : [];
        setSheets(loadedSheets);

        // ✅ Show all sheets (no longer filtering by usage)
        console.log(`📋 Hojas cargadas: ${loadedSheets.length}`);

        // Procesar templates
        if (tRes.status === "fulfilled") setTemplates(tRes.value.data || []);

        // Procesar Meta templates (para Marketing) - incluir APPROVED y PENDING
        if (mRes.status === "fulfilled" && mRes.value.data?.templates) {
          console.log('📧 Raw Meta templates response:', mRes.value.data.templates);
          const localTemplates =
            tRes.status === "fulfilled"
              ? (tRes.value.data || [])
              : [];

          const availableTemplates = mRes.value.data.templates
            .filter((t: any) => t.status === 'APPROVED')
            .map((t: any) =>
              normalizeMetaTemplate(t, localTemplates)
            );

          console.log('✅ Filtered metaTemplates:', availableTemplates);
          setMetaTemplates(availableTemplates);
        } else {
          console.log('⚠️ No Meta templates in response, using local templates as fallback');
          console.log('📊 tRes status:', tRes.status);
          console.log('📊 tRes value data:', tRes.status === "fulfilled" ? tRes.value.data : 'rejected');
          // Fallback: use local templates from DB if Meta API fails (for local dev)
          // Note: local templates don't have status field in API response, so we include all
          if (tRes.status === "fulfilled" && tRes.value.data?.length > 0) {
            const localTemplates = tRes.value.data.map((t: any) =>
              normalizeLocalTemplate(t, {
                defaultCategory: 'MARKETING',
              })
            );

            console.log('📋 Using local templates as fallback:', localTemplates);
            setMetaTemplates(localTemplates);
          } else {
            console.log('❌ No local templates found or tRes rejected');
          }
        }

        // ✅ Auto-seleccionar la primera hoja (más reciente) — skip for Calendar campaigns
        const isCalendarFlow =
          localStorage.getItem('renewal_create_campaign_type') === 'Calendar' ||
          campaignType === 'Calendar';
        if (!isCalendarFlow && loadedSheets.length > 0 && !googleSheetId) {
          // Ordenar por fecha más reciente
          const sortedByRecent = [...loadedSheets].sort((a: any, b: any) => {
            const aTime = new Date(a.created_at || a.createdAt || 0).getTime();
            const bTime = new Date(b.created_at || b.createdAt || 0).getTime();
            return bTime - aTime;
          });

          // Seleccionar la primera hoja directamente
          const firstSheet = sortedByRecent[0];
          const selectedId = String(firstSheet.sheet_id ?? firstSheet.id ?? firstSheet._id);
          console.log('🎯 Auto-seleccionando primera hoja:', selectedId);

          if (selectedId) {
            setGoogleSheetId(selectedId);
            setGoogleSheetTabId(null);
            setGoogleSheetTabName("");
            setSheetColumns(null);
            setSampleRow(null);
            setPhoneColumn("");
            setRenewalDateColumn("");
          }
        }
      } catch (err) {
        console.error("Error cargando recursos:", err);
      } finally {
        setLoadingAssets(false);
      }
    })();
  }, []); // ✅ Solo ejecutar una vez al montar

  // If initialData is provided (editing mode), populate form state
  useEffect(() => {
    if (!initialData) return;
    try {
      setName(initialData.name || "");
      setGoogleSheetId(initialData.google_sheet_id || initialData.googleSheetId || "");
      setGoogleSheetTabId(initialData.google_sheet_tab_id ?? initialData.googleSheetTabId ?? null);
      setGoogleSheetTabName(initialData.google_sheet_tab_name || initialData.googleSheetTabName || "");
      setPhoneColumn(initialData.phone_column || initialData.phoneColumn || "");
      setRenewalDateColumn(initialData.renewal_date_column || initialData.renewalDateColumn || "");
      setMessageTemplate(initialData.message_template || initialData.message || "");
      setTemplateId(initialData.template_id ?? initialData.templateId ?? null);
      if (initialData.renewal_config?.periodicity) {
        const p = initialData.renewal_config.periodicity;
        const unit = normalizePeriodicityUnit(p.unit);
        const value = Number(p.value);
        if (!Number.isNaN(value) && value > 0) {
          setPeriodicityValue(value);
          setPeriodicityUnit(unit);
          setIsCustomPeriodicity(!isPresetPeriodicity(unit, value));
        }
      }
      if (Array.isArray(initialData.renewal_config?.reminder_days_before)) {
        setReminderDays(initialData.renewal_config.reminder_days_before.slice());
      } else if (Array.isArray(initialData.reminder_days)) {
        setReminderDays(initialData.reminder_days.slice());
      }
      if (initialData.renewal_config?.sending_time) setSendingTime(initialData.renewal_config.sending_time);
      // Keep the step at info so user can review
    } catch (e) {
      console.debug('Error applying initialData to form', e);
    }
  }, [initialData]);

  // Auto-select sheet and columns (Sheets campaigns only)
  useEffect(() => {
    if (campaignType === 'Calendar' || step === 'calendar') return;

    if (!googleSheetId && Array.isArray(sheets) && sheets.length > 0) {
      const id = selectMostRecentSheet(sheets);

      if (id) {
        setGoogleSheetId(String(id));
        setGoogleSheetTabId(null);
        setGoogleSheetTabName("");
        setSheetColumns(null);
        setSampleRow(null);
      }
    }
  }, [sheets, googleSheetId, campaignType, step]);

  
  useEffect(() => {
    const requestId = ++columnRequestRef.current;

    if (campaignType === "Calendar" || step === "calendar") {
      if (!googleSheetId) {
        setSheetColumns(null);
        setSampleRow(null);
      }
      return;
    }

    if (!googleSheetId || !googleSheetTabName) {
      setSheetColumns(null);
      setSampleRow(null);
      setColumnCheck(null);
      return;
    }

    let cancelled = false;

    const sheetId = googleSheetId;
    const tabName = googleSheetTabName;
    const tabId = googleSheetTabId ?? null;

    const updateStatus = (status: "loading" | "loaded" | "error") => {
      setColumnCheck({ sheetId, tabName, tabId, status });
    };

    setSheetColumns(null);
    setSampleRow(null);
    updateStatus("loading");

    const validateColumns = async () => {
      try {
        const data = await fetchSheetData(sheetId, tabName, tabId);

        if (cancelled || columnRequestRef.current !== requestId) return;

        if (!data) {
          updateStatus("error");
          return;
        }

        const columns = data.columns ?? [];

        setSheetColumns(columns);
        setSampleRow(data.sampleRow ?? null);
        updateStatus("loaded");

        if (columns.length > 0) {
          const { phone, date } = autoSelectColumns(
            columns,
            phoneColumn,
            renewalDateColumn
          );

          setPhoneColumn(phone);
          setRenewalDateColumn(date);
        }
      } catch (error) {
        if (cancelled || columnRequestRef.current !== requestId) return;

        console.error("Error validando columnas de Google Sheets:", error);
        setSheetColumns(null);
        setSampleRow(null);
        updateStatus("error");
      }
    };

    void validateColumns();

    return () => {
      cancelled = true;
      columnRequestRef.current += 1;
    };
  }, [googleSheetId, googleSheetTabId, googleSheetTabName, campaignType, step]);


  // ✅ Load templates and tabs in use for the selected sheet
  useEffect(() => {
    if (campaignType === 'Calendar' || step === 'calendar' || !googleSheetId) {
      setTemplatesInUseForSheet([]);
      setTabNamesInUseForSheet([]);
      setTabIdsInUseForSheet([]);
      return;
    }

    (async () => {
      try {
        const editingCampaignId = initialData?.id || initialData?._id;
        const usageUrl = editingCampaignId
          ? `/templates/in-use-by-sheet?sheet_id=${encodeURIComponent(googleSheetId)}&exclude_campaign_id=${encodeURIComponent(String(editingCampaignId))}`
          : `/templates/in-use-by-sheet?sheet_id=${encodeURIComponent(googleSheetId)}`;

        const res = await api.get(usageUrl);
        const templateIds = res.data?.template_ids || [];
        const tabNames = res.data?.tab_names_in_use || [];
        const tabIds = res.data?.tab_ids_in_use || [];

        setTemplatesInUseForSheet(templateIds);
        setTabNamesInUseForSheet(tabNames);
        setTabIdsInUseForSheet(tabIds);

        console.log(`📋 Templates en uso para hoja ${googleSheetId}:`, templateIds);
        console.log(`📑 Pestañas en uso para hoja ${googleSheetId}:`, tabNames);
        console.log(`🆔 IDs de pestañas en uso para hoja ${googleSheetId}:`, tabIds);
      } catch (err) {
        console.warn("⚠️ Error obteniendo plantillas y pestañas en uso:", err);
        setTemplatesInUseForSheet([]);
        setTabNamesInUseForSheet([]);
        setTabIdsInUseForSheet([]);
      }
    })();
    }, [googleSheetId, campaignType, step, initialData?.id, initialData?._id]);

  // Handlers
  // Note: 'info' step removed; validation moved to preview/create if needed.

  const handleNextFromInfo = async () => {
    if (!name || !name.trim()) return toast.error("Introduce un nombre para la campaña");
    if (!campaignType) return toast.error("Selecciona un tipo de campaña");

    setCheckingCampaignName(true);
    setNameConflictError(null);
    try {
      const res = await api.get<{
        available: boolean;
        template_name?: string;
        suggested_campaign_name?: string;
      }>(`/templates/check-campaign-name?name=${encodeURIComponent(name.trim())}`);
      if (res.data && res.data.available === false) {
        const msg =
          "Ese nombre de campaña ya existe porque la plantilla usaría el mismo nombre. Cambia el nombre.";
        setNameConflictError(msg);
        toast.error(msg);
        return;
      }
    } catch (err) {
      console.warn("check-campaign-name failed, continuing:", err);
      // Soft-fail: do not block wizard if the check endpoint is unavailable
    } finally {
      setCheckingCampaignName(false);
    }

    if (campaignType === "Dynamic") {
      setStep("dynamic_config");
      return;
    }

    if (campaignType === "Calendar") {
      localStorage.setItem('renewal_create_campaign_type', 'Calendar');
      setStep("calendar");
      return;
    }

    // Only reset tour flags if the user has never completed the tour
    // (prevents tour from re-triggering on every Info→Data navigation)
    const tourCompleted = localStorage.getItem('tour_permanently_dismissed');
    if (!tourCompleted) {
      sessionStorage.removeItem('renewal_tour_stage1_seen');
      sessionStorage.removeItem('renewal_tour_stage2_seen');
      sessionStorage.removeItem('renewal_tour_stage_columns_seen');
      sessionStorage.removeItem('renewal_tour_stage_scheduling_seen');
      sessionStorage.removeItem('renewal_tour_stage_reminders_seen');
    }

    setStep("data");
  };

  const handleNextFromDynamicConfig = () => {
    if (!googleSheetId) return toast.error("Selecciona la Google Sheet");
    if (!googleSheetTabName) return toast.error("Selecciona la pestaña de Google Sheets");
    if (!phoneColumn) return toast.error("Selecciona la columna de teléfono");
    if (!dynamicDateColumn) return toast.error("Selecciona la columna de fecha dinámica");
    setStep("template");
  };

  const handleNextFromData = () => {
    if (!googleSheetId) return toast.error("Selecciona la Google Sheet");
    if (!googleSheetTabName) return toast.error("Selecciona la pestaña de Google Sheets");
    if (!phoneColumn) return toast.error("Selecciona la columna de teléfono");

    // For renewal campaigns, require renewalDateColumn
    if (campaignType !== "Marketing" && !renewalDateColumn) {
      return toast.error("Selecciona la columna de fecha de renovación");
    }

    setStep("template");
  };

  // Marketing-specific: skip template/message steps and go to preview
  const handleNextFromDataMarketing = () => {
    if (!googleSheetId) return toast.error("Selecciona la Google Sheet");
    if (!googleSheetTabName) return toast.error("Selecciona la pestaña de Google Sheets");
    if (!phoneColumn) return toast.error("Selecciona la columna de teléfono");
    // Solo validar fecha/hora si NO es envío inmediato
    if (!sendImmediately) {
      if (!sendDate) return toast.error("Selecciona la fecha de envío");
      if (!sendingTime) return toast.error("Selecciona la hora de envío");
    }
    setStep("template");  // Go to template step for Marketing
  };

  const handleCreate = async () => {
    // ✅ Validar que fecha y hora de envío sean en el futuro (solo para Marketing y si NO es envío inmediato)
    if (campaignType === "Marketing" && !sendImmediately && sendDate && sendingTime) {
      const selectedDateTime = new Date(`${sendDate}T${sendingTime}:00`);
      const now = new Date();
      if (selectedDateTime <= now) {
        toast.error("La hora de envío ya pasó. Por favor actualiza la fecha y hora.");
        setStep("data");  // Volver al paso de datos para corregir
        return;
      }
    }

    let finalTemplateId = templateId;
    let finalTemplateName: string | undefined = selectedMetaTemplate || undefined; // Nombre de plantilla seleccionada o creada
    const isNewTemplateDraft = !templateId && !!templateNameInput && !!messageTemplate;

    // If no templateId but we have templateNameInput and messageTemplate, create template in Meta first
    if (isNewTemplateDraft) {
      try {
        setSaving(true);
        toast.info("Creando plantilla en Meta...");

        const metaTemplateName = normalizeMetaTemplateName(templateNameInput);

        if (!metaTemplateName || metaTemplateName.length === 0) {
          toast.error("El nombre de la plantilla debe contener letras o números (no solo caracteres especiales).");
          setSaving(false);
          return;
        }

        const draftValidation = validateMetaTemplateDraft({
          bodyText: messageTemplate,
          headerEnabled: templateHeaderEnabled,
          headerType: templateHeaderType,
          headerText: templateHeaderText,
          headerImageHandle: templateHeaderImageHandle,
          footerEnabled: templateFooterEnabled,
          footerText: templateFooterText,
          buttonsEnabled: templateButtonsEnabled,
          buttons: templateButtons,
        });

        if (!draftValidation.valid) {
          toast.error(draftValidation.message);
          setSaving(false);
          return;
        }

        const metaTemplateRequest = buildMetaTemplateRequest({
          name: templateNameInput,
          language: templateLanguage,
          category: templateType,
          bodyText: messageTemplate,
          headerEnabled: templateHeaderEnabled,
          headerType: templateHeaderType,
          headerText: templateHeaderText,
          headerImageHandle: templateHeaderImageHandle,
          headerImageUrl: templateHeaderImageUrl,
          footerEnabled: templateFooterEnabled,
          footerText: templateFooterText,
          buttonsEnabled: templateButtonsEnabled,
          buttons: templateButtons,
        });

        console.log("📤 Creating template in Meta:", metaTemplateRequest);

        const templateResponse = await api.post('/whatsapp/templates/create', metaTemplateRequest);

        if (templateResponse.data.success) {
          finalTemplateId = templateResponse.data.template_id;
          finalTemplateName = metaTemplateName; // ✅ Guardar nombre para incluirlo en el payload de la campaña
          console.log("✅ Template created in Meta:", finalTemplateId, "Name:", finalTemplateName, "Status:", templateResponse.data.status);
          toast.success(`Plantilla "${metaTemplateName}" creada (Estado: ${templateResponse.data.status})`);
        } else {
          throw new Error(templateResponse.data.error || "Error al crear plantilla");
        }
      } catch (err: any) {
        console.error("Error creating template in Meta:", err);
        const errorMsg = err?.response?.data?.detail || err?.response?.data?.error_user_msg || err.message || "Error al crear plantilla en Meta";
        toast.error(errorMsg);
        setSaving(false);
        return; // Stop here, don't create campaign
      }
    }

    // Now create the campaign with the template_id
    // Determine if this is a Marketing (broadcast) or Renewal campaign
    const isMarketing = campaignType === "Marketing";

    let finalMessageTemplate = messageTemplate.trim();
    let finalColumnMapping = columnMapping || {};

    if (isNewTemplateDraft) {
      const mappingResult = buildSheetColumnMapping({ bodyText: messageTemplate });
      if (mappingResult.missingKeys.length > 0) {
        toast.error("No se pudo resolver el mapeo de variables del mensaje. Revisa los datos de tu plantilla.");
        setSaving(false);
        return;
      }
      finalMessageTemplate = mappingResult.messageTemplate;
      finalColumnMapping = mappingResult.columnMapping;
    }

    try {
      setSaving(true);

      if (isMarketing) {
        // ✅ MARKETING CAMPAIGN - Use /campaigns endpoint (broadcast)
        // Si es envío inmediato, usar la fecha/hora actual
        let sendAtISO: string | undefined;
        if (sendImmediately) {
          // Envío inmediato: no establecer send_at, el backend lo manejará
          sendAtISO = undefined;
        } else {
          sendAtISO = sendDate && sendingTime
            ? new Date(`${sendDate}T${sendingTime}:00`).toISOString()
            : undefined;
        }

        const marketingPayload = {
          name: name.trim(),
          type: "broadcast",
          google_sheet_id: googleSheetId,
          google_sheet_tab_id: googleSheetTabId ?? undefined,
          google_sheet_tab_name: googleSheetTabName || undefined,
          phone_column: phoneColumn,
          country_code: countryCode,
          template_id: finalTemplateId ?? undefined,
          message_template: finalMessageTemplate || undefined,
          send_at: sendAtISO,
          send_immediately: sendImmediately,  // ✅ NUEVO: Indicar envío inmediato
          whatsapp_template_name: finalTemplateName || undefined, // ✅ FIX: Usa nombre de plantilla nueva O seleccionada
          whatsapp_template_language: "es_MX",
          column_mapping: finalColumnMapping,
          header_image_url: templateHeaderType === 'IMAGE' && templateHeaderImageUrl ? templateHeaderImageUrl : undefined // ✅ Persist URL
        };

        console.log("📤 Creating MARKETING (broadcast) campaign:", marketingPayload);

        // If editing existing campaign
        if (initialData && (initialData.id || initialData._id)) {
          const id = initialData.id || initialData._id;
          const resUpdate = await api.patch(`/campaigns/${id}`, marketingPayload);
          toast.success("Campaña de Marketing actualizada");
          if (onCreated) onCreated(String(id));
        } else {
          const resCreate = await api.post("/campaigns", marketingPayload);
          const newId = resCreate?.data?.id || resCreate?.data?.campaign_id || null;
          const campaignStatus = resCreate?.data?.status;

          if (campaignStatus === "pending_template_approval") {
            // ✅ Mostrar modal de feedback - guardar ID para después
            setPendingApprovalInfo({
              campaignName: name.trim(),
              templateName: templateNameInput || "Nueva plantilla",
              campaignType: "Marketing"
            });
            setPendingCampaignId(newId);
            setShowPendingApprovalModal(true);
            setSaving(false);
            // NO llamar onCreated aquí - se llamará cuando el usuario cierre el modal
            return; // Salir temprano para evitar redirección
          } else {
            toast.success("Campaña de Marketing creada y activada");
            if (onCreated && newId) onCreated(newId);
          }
        }

        // Restore URL to campaigns list (solo si no se mostró el modal)
        try {
          window.history.replaceState({}, "", "/campaigns");
        } catch (e) {
          console.debug("No se pudo restaurar la URL:", e);
        }

      } else if (campaignType === "Dynamic") {
        // ✅ DYNAMIC CAMPAIGN - Use /dynamics endpoint
        const dynamicPayload = {
          name: name.trim(),
          google_sheet_id: googleSheetId,
          google_sheet_tab_id: googleSheetTabId ?? undefined,
          google_sheet_tab_name: googleSheetTabName || undefined,
          phone_column: phoneColumn,
          time_column: dynamicTimeColumn || undefined,  // Optional - defaults to 9 AM if not provided
          date_column: dynamicDateColumn || undefined,
          country_code: countryCode,
          template_id: finalTemplateId ?? undefined,
          whatsapp_template_name: finalTemplateName || undefined, // ✅ FIX: Incluir nombre de plantilla
          message_template: finalMessageTemplate || undefined,
          column_mapping: finalColumnMapping,
          // ❌ NO enviar header_image_url aquí - ya está en el template guardado
          dynamic_config: {
            time_column: dynamicTimeColumn || undefined,
            date_column: dynamicDateColumn || undefined,
            timezone: "America/Bogota",
            // ✅ NUEVO: Configuración de Modo Citas
            is_appointment_mode: isAppointmentMode,
            use_default_reminders: useDefaultReminders,
            reminder_days_before: dynamicReminderDays,
            reminder_hours_before: dynamicReminderHours
          }
        };

        console.log("📤 Creating DYNAMIC campaign:", dynamicPayload);
        console.log("🔧 DEBUG REMINDERS:", {
          isAppointmentMode,
          useDefaultReminders,
          dynamicReminderDays,
          dynamicReminderHours,
          config_in_payload: dynamicPayload.dynamic_config
        });

        // If editing existing campaign
        if (initialData && (initialData.id || initialData._id)) {
          const id = initialData.id || initialData._id;
          const resUpdate = await api.patch(`/dynamics/${id}`, dynamicPayload);
          toast.success("Campaña Dinámica actualizada");
          if (onCreated) onCreated(String(id));
        } else {
          const resCreate = await api.post("/dynamics", dynamicPayload);
          const newId = resCreate?.data?.id || resCreate?.data?.campaign_id || null;
          const campaignStatus = resCreate?.data?.status;

          if (campaignStatus === "pending_template_approval") {
            // ✅ Mostrar modal de feedback - guardar ID para después
            setPendingApprovalInfo({
              campaignName: name.trim(),
              templateName: templateNameInput || "Nueva plantilla",
              campaignType: "Dinámica"
            });
            setPendingCampaignId(newId);
            setShowPendingApprovalModal(true);
            setSaving(false);
            // NO llamar onCreated aquí - se llamará cuando el usuario cierre el modal
            return; // Salir temprano para evitar redirección
          } else {
            toast.success("Campaña Dinámica creada y activada");
            if (onCreated && newId) onCreated(newId);
          }
        }

        // Restore URL to renewals list (solo si no se mostró el modal)
        try {
          window.history.replaceState({}, "", "/renewals");
        } catch (e) {
          console.debug("No se pudo restaurar la URL:", e);
        }

      } else if (campaignType === "Calendar") {
        // ✅ CALENDAR CAMPAIGN - Use /calendar-campaigns endpoint
        const calendarPayload = {
          name: name.trim(),
          calendar_id: googleSheetId || "",  // Reuse googleSheetId state for calendar_id
          country_code: countryCode,
          template_id: finalTemplateId ?? undefined,
          message_template: finalMessageTemplate || undefined,
          column_mapping: columnMapping || {},
          // ❌ NO enviar header_image_url aquí - ya está en el template guardado
          calendar_config: {
            calendar_id: googleSheetId || "",
            timezone: "America/Bogota",
            use_default_reminders: useDefaultReminders,
            reminder_days_before: dynamicReminderDays,
            reminder_hours_before: dynamicReminderHours
          }
        };

        console.log("📤 Creating CALENDAR campaign:", calendarPayload);

        const resCreate = await api.post("/calendar-campaigns", calendarPayload);
        const newId = resCreate?.data?.id || resCreate?.data?.campaign_id || null;
        const campaignStatus = resCreate?.data?.status;

        if (campaignStatus === "pending_template_approval") {
          setPendingApprovalInfo({
            campaignName: name.trim(),
            templateName: templateNameInput || "Nueva plantilla",
            campaignType: "Calendar"
          });
          setPendingCampaignId(newId);
          setShowPendingApprovalModal(true);
          setSaving(false);
          return;
        } else {
          toast.success("Campaña Calendar creada y activada");
          if (onCreated && newId) onCreated(newId);
        }

        try {
          window.history.replaceState({}, "", "/renewals");
        } catch (e) {
          console.debug("No se pudo restaurar la URL:", e);
        }

      } else {
        // ✅ RENEWAL CAMPAIGN - Use /renewals endpoint
        const periodicityError = validatePeriodicityValue(periodicityUnit, periodicityValue);
        if (periodicityError) {
          toast.error(periodicityError);
          setSaving(false);
          return;
        }

        // Si periodicidad es DAYS y no hay recordatorios, usar [0] (mismo día)
        const finalReminderDays = reminderDays.length > 0
          ? parseReminderDays(reminderDays)
          : (periodicityUnit === 'DAYS' ? [0] : []);

        const renewalPayload = {
          name: name.trim(),
          google_sheet_id: googleSheetId,
          google_sheet_tab_id: googleSheetTabId ?? undefined,
          google_sheet_tab_name: googleSheetTabName || undefined,
          phone_column: phoneColumn,
          country_code: countryCode,
          template_id: finalTemplateId ?? undefined,
          whatsapp_template_name: finalTemplateName || undefined, // ✅ FIX: Incluir nombre de plantilla
          message_template: finalMessageTemplate || undefined,
          column_mapping: finalColumnMapping,
          // ❌ NO enviar header_image_url aquí - ya está en el template guardado
          renewal_config: {
            periodicity: { value: Number(periodicityValue), unit: periodicityUnit.toLowerCase() as "months" | "years" | "days" | "weeks" },
            reminder_days_before: finalReminderDays,
            sending_time: sendingTime,
            renewal_date_column: renewalDateColumn
          }
        };

        console.log("📤 Creating RENEWAL campaign:", renewalPayload);

        // If editing existing renewal
        if (initialData && (initialData.id || initialData._id)) {
          const id = initialData.id || initialData._id;
          const resUpdateRenewal = await api.patch(`/renewals/${id}`, renewalPayload);
          toast.success("Campaña de renovación actualizada");
          if (onCreated) onCreated(String(id));
        } else {
          const resCreateRenewal = await api.post("/renewals", renewalPayload);
          const newId = resCreateRenewal?.data?.id || resCreateRenewal?.data?.campaign_id || null;
          const campaignStatus = resCreateRenewal?.data?.status;

          if (campaignStatus === "pending_template_approval") {
            // ✅ Mostrar modal de feedback - guardar ID para después
            setPendingApprovalInfo({
              campaignName: name.trim(),
              templateName: templateNameInput || "Nueva plantilla",
              campaignType: "Renovación"
            });
            setPendingCampaignId(newId);
            setShowPendingApprovalModal(true);
            setSaving(false);
            // NO llamar onCreated aquí - se llamará cuando el usuario cierre el modal
            return; // Salir temprano para evitar redirección
          } else {
            toast.success("Campaña de renovación creada y activada");
            if (onCreated && newId) onCreated(newId);
          }
        }

        // Restore URL to renewals list (solo si no se mostró el modal)
        try {
          window.history.replaceState({}, "", "/renewals");
        } catch (e) {
          console.debug("No se pudo restaurar la URL a /renewals:", e);
        }
      }

    } catch (err: any) {
      console.error("Error creando/actualizando campaña:", err);
      const msg = err?.response?.data?.detail || "No se pudo crear/actualizar la campaña";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Si el hook recibió un onCancel, llámalo.
    try {
      if (onCancel) onCancel();
    } catch (e) {
      console.debug("Error ejecutando onCancel:", e);
    }
    // Restaurar URL a la lista de renovaciones
    try {
      window.history.replaceState({}, "", "/renewals");
    } catch (e) {
      console.debug("No se pudo restaurar la URL a /renewals al cancelar:", e);
    }
  };

  const reloadSheets = async (): Promise<any[]> => {
    try {
      const response = await api.get("/google-sheets");
      const updatedSheets = Array.isArray(response.data) ? response.data : [];

      setSheets(updatedSheets);

      return updatedSheets;
    } catch (error) {
      console.error("Error recargando hojas de Google Sheets:", error);
      toast.error("No se pudieron actualizar las hojas de Google Sheets");
      return [];
    }
  };
  
  const selectSheetTab = (tabName: string, tabId?: number | null) => {
    setGoogleSheetTabId(tabId ?? null);
    setGoogleSheetTabName(tabName);
    setSheetColumns(null);
    setSampleRow(null);
    setPhoneColumn("");
    setRenewalDateColumn("");
    setColumnMapping({});
  };

  const selectSheet = async (sheetId: string) => {
    // Seleccionar archivo de Google Sheets.
    // La pestaña interna se seleccionará después.
    setGoogleSheetId(sheetId);
    setGoogleSheetTabId(null);
    setGoogleSheetTabName("");
    setSheetColumns(null);
    setSampleRow(null);
    setPhoneColumn("");
    setRenewalDateColumn("");
  };

  const reloadTemplates = async () => {
    try {
      const res = await api.get("/templates");
      setTemplates(res.data || []);
    } catch (err) {
      console.error("Error recargando plantillas:", err);
    }
  };

  const handleTemplateCreated = async (newTemplateId?: string) => {
    setShowTemplateModal(false);
    await reloadTemplates();
    if (newTemplateId) {
      setTemplateId(newTemplateId);
      toast.success("Plantilla creada y seleccionada");
    }
  };

  const addReminderFromInput = (input?: string) => {
    const raw = (input ?? reminderInput ?? "").trim();
    if (!raw) return;
    const parts = raw.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    const nums = parts.map(p => Number.parseInt(p, 10)).filter(n => !Number.isNaN(n) && n >= 0);
    if (nums.length === 0) {
      toast.error("Ingresa al menos un número de días válido");
      return;
    }
    setReminderDays(prev => Array.from(new Set([...nums, ...prev])).sort((a, b) => b - a));
    setReminderInput("");
  };

  const removeReminder = (value: number) => {
    setReminderDays(prev => prev.filter(v => v !== value));
  };

  // Clear all reminder days (used when periodicity changes)
  const clearReminderDays = () => {
    setReminderDays([]);
    setReminderInput("");
  };

  // Handler for periodicity change - clears reminder days since max allowed days changes
  const handlePeriodicityChange = (unit: "MONTHS" | "YEARS" | "DAYS" | "WEEKS", value: number) => {
    // Only clear reminders if the periodicity actually changed
    if (unit !== periodicityUnit || value !== periodicityValue || isCustomPeriodicity) {
      setIsCustomPeriodicity(false);
      setPeriodicityUnit(unit);
      setPeriodicityValue(value);
      // Clear reminder days since the max allowed days will change
      clearReminderDays();
    }
  };

  const handleEnableCustomPeriodicity = () => {
    if (!isCustomPeriodicity) {
      setIsCustomPeriodicity(true);
      // Custom supports days/weeks/months only (Anual covers years via preset)
      const nextUnit: "MONTHS" | "YEARS" | "DAYS" | "WEEKS" =
        periodicityUnit === "YEARS" ? "MONTHS" : periodicityUnit;
      const nextValue = Math.min(Math.max(periodicityValue || 1, 1), getCustomPeriodicityMax(nextUnit));
      if (nextUnit !== periodicityUnit || nextValue !== periodicityValue) {
        clearReminderDays();
      }
      setPeriodicityUnit(nextUnit);
      setPeriodicityValue(nextValue);
    }
  };

  const handleDisableCustomPeriodicity = () => {
    if (isCustomPeriodicity) {
      setIsCustomPeriodicity(false);
      clearReminderDays();
    }
  };

  const handleCustomPeriodicityChange = (unit: "MONTHS" | "YEARS" | "DAYS" | "WEEKS", value: number) => {
    // Personalizado no ofrece años; usar meses si llega YEARS
    const safeUnit = unit === "YEARS" ? "MONTHS" : unit;
    // No clamp to max: allow high numbers in UI; validation blocks continue
    const next = Number.isFinite(value) ? Math.max(Math.trunc(value), 0) : 0;
    if (safeUnit !== periodicityUnit || next !== periodicityValue) {
      setIsCustomPeriodicity(true);
      setPeriodicityUnit(safeUnit);
      setPeriodicityValue(next);
      clearReminderDays();
    } else {
      setIsCustomPeriodicity(true);
    }
  };

  
  // Reload columns for the currently selected sheet
  const reloadColumns = async () => {
    if (!googleSheetId || !googleSheetTabName || campaignType === "Calendar") {
      return;
    }

    const sheetId = googleSheetId;
    const tabName = googleSheetTabName;
    const tabId = googleSheetTabId ?? null;
    const requestId = ++columnRequestRef.current;

    setSheetColumns(null);
    setSampleRow(null);
    setColumnCheck({ sheetId, tabName, tabId, status: "loading" });

    try {
      const data = await fetchSheetData(sheetId, tabName, tabId);

      if (columnRequestRef.current !== requestId) return;

      if (!data) {
        setColumnCheck({ sheetId, tabName, tabId, status: "error" });
        return;
      }

      const columns = data.columns ?? [];

      setSheetColumns(columns);
      setSampleRow(data.sampleRow ?? null);
      setColumnCheck({ sheetId, tabName, tabId, status: "loaded" });

      if (columns.length > 0) {
        const { phone, date } = autoSelectColumns(
          columns,
          phoneColumn,
          renewalDateColumn
        );

        setPhoneColumn(phone);
        setRenewalDateColumn(date);
      }
    } catch (error) {
      if (columnRequestRef.current !== requestId) return;

      console.error("Error actualizando columnas:", error);
      setSheetColumns(null);
      setSampleRow(null);
      setColumnCheck({ sheetId, tabName, tabId, status: "error" });
    }
  };



  // Handler para cerrar el modal de aprobación pendiente
  const handleClosePendingApprovalModal = () => {
    setShowPendingApprovalModal(false);

    // Llamar onCreated con el ID guardado
    if (onCreated && pendingCampaignId) {
      onCreated(pendingCampaignId);
    }

    // Restaurar URL según el tipo de campaña
    try {
      const url = campaignType === "Marketing" ? "/campaigns" : "/renewals";
      window.history.replaceState({}, "", url);
    } catch (e) {
      console.debug("No se pudo restaurar la URL:", e);
    }

    // Limpiar estados
    setPendingApprovalInfo(null);
    setPendingCampaignId(null);
  };

  return {
    // States
    step, setStep, saving,
    name, setName, googleSheetId, setGoogleSheetId, googleSheetTabId, setGoogleSheetTabId, googleSheetTabName, setGoogleSheetTabName, phoneColumn, setPhoneColumn, renewalDateColumn, setRenewalDateColumn, dynamicDateColumn, setDynamicDateColumn, dynamicTimeColumn, setDynamicTimeColumn,
    campaignType, setCampaignType,
    nameConflictError, setNameConflictError, checkingCampaignName,
    sheetColumns, columnValidationStatus, sampleRow, countryCode, setCountryCode, messageTemplate, setMessageTemplate, templateId, setTemplateId,
    periodicityValue, setPeriodicityValue, periodicityUnit, setPeriodicityUnit, reminderDays, setReminderDays,
    reminderInput, setReminderInput,
    isCustomPeriodicity, setIsCustomPeriodicity,
    // ✅ New: Appointment Mode states
    isAppointmentMode, setIsAppointmentMode,
    useDefaultReminders, setUseDefaultReminders,
    dynamicReminderDays, setDynamicReminderDays,
    dynamicReminderHours, setDynamicReminderHours,
    sendingTime, setSendingTime,
    sheets, templates, loadingAssets, showTemplateModal, setShowTemplateModal, showTutorialModal, setShowTutorialModal,
    searchQuery, setSearchQuery, currentPage, setCurrentPage, visibleSheets, total, totalPages, start,
    templateType, setTemplateType, templateLanguage, setTemplateLanguage, templateNameInput, setTemplateNameInput,
    // ✅ Template components (header, footer, buttons)
    templateHeaderText, setTemplateHeaderText, templateFooterText, setTemplateFooterText, templateButtons, setTemplateButtons,
    // ✅ Header image support
    templateHeaderType, setTemplateHeaderType, templateHeaderImageHandle, setTemplateHeaderImageHandle, templateHeaderImageUrl, setTemplateHeaderImageUrl, templateHeaderImagePreview, setTemplateHeaderImagePreview,
    // ✅ Toggle states for template sections
    templateHeaderEnabled, setTemplateHeaderEnabled, templateFooterEnabled, setTemplateFooterEnabled, templateButtonsEnabled, setTemplateButtonsEnabled,
    // Marketing-specific
    metaTemplates, loadingMetaTemplates, selectedMetaTemplate, setSelectedMetaTemplate,
    sendDate, setSendDate, sendImmediately, setSendImmediately, columnMapping, setColumnMapping,
    // ✅ Available templates and tabs (filtered by sheet usage)
    availableMetaTemplates, tabNamesInUseForSheet, tabIdsInUseForSheet,
    // ✅ Pending approval modal state
    showPendingApprovalModal, setShowPendingApprovalModal, pendingApprovalInfo, handleClosePendingApprovalModal,
    // Handlers
    handleNextFromInfo, handleNextFromDynamicConfig, handleNextFromData, handleNextFromDataMarketing, handleCreate, handleCancel, selectSheet, selectSheetTab, reloadSheets, reloadTemplates, handleTemplateCreated,
    addReminderFromInput, removeReminder, handlePeriodicityChange, handleEnableCustomPeriodicity, handleDisableCustomPeriodicity, handleCustomPeriodicityChange, reloadColumns
  };
};
